import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  REVIEWER_NAME_MAX,
  REVIEW_TEXT_MAX,
  REVIEW_TEXT_MIN,
  type IpBlockInfo,
  type ReviewPageData,
  longDate,
} from '@astrologermatch/shared';
import { Turnstile } from '../components/Turnstile.js';
import { api, ApiError } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';
import { useSettings } from '../lib/state.js';
import { useAdoptEarlyInput } from '../lib/earlyInput.js';
import { useHydrated } from '../lib/hydrated.js';

// Ported from prototype/src/pages/ReviewPage.jsx. The prototype's simulated-IP box is gone: the
// server reads the real client IP (via Nginx) and applies the IP rule.

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="wrap page-pad">
      <div className="card review-card">{children}</div>
    </div>
  );
}

export function ReviewPage({ data }: { data: ReviewPageData }) {
  // Rendered by the page switch (not a <Route path="/review/:token">), so read the token from the
  // path itself — useParams() would be empty here (BUG-006).
  const { pathname } = useLocation();
  const token = decodeURIComponent(pathname.split('/review/')[1]?.split('/')[0] ?? '');
  const settings = useSettings();
  const hydrated = useHydrated();
  const [rating, setRating] = useState(0);
  const [hoverR, setHoverR] = useState(0);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  useAdoptEarlyInput(nameRef, setName);
  useAdoptEarlyInput(textRef, setText);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [blocked, setBlocked] = useState(data.ipBlock ?? null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  useSeo({ title: 'Leave a review', robots: 'noindex,nofollow' });

  const a = data.astrologer;

  if (done) {
    return (
      <Shell>
        <div className="center">
          <div className="big-check" aria-hidden="true">
            ✓
          </div>
          <h1 className="h2">Thank you!</h1>
          <p className="muted">Your review for {a?.name} has been published.</p>
          {a && (
            <Link to={`/astrologers/${a.slug}`} className="btn btn-primary">
              See their profile
            </Link>
          )}
        </div>
      </Shell>
    );
  }
  if (data.status === 'invalid' || !a) {
    return (
      <Shell>
        <h1 className="h2">Link not valid</h1>
        <p className="muted">
          This review link doesn&apos;t exist. Please ask the astrologer for a new one.
        </p>
      </Shell>
    );
  }
  if (data.status === 'used') {
    return (
      <Shell>
        <h1 className="h2">This link has already been used</h1>
        <p className="muted">
          Each review link works only once. If you haven&apos;t left a review yet, please ask{' '}
          {a.name} for a new link.
        </p>
      </Shell>
    );
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setErr('');
    if (!rating) {
      setErr('Please choose a star rating.');
      return;
    }
    // Focus goes to the field that needs fixing (LANE-D-17).
    if (!name.trim()) {
      setErr('Please enter your name.');
      nameRef.current?.focus();
      return;
    }
    if (text.trim().length < REVIEW_TEXT_MIN) {
      setErr(`Please write at least ${String(REVIEW_TEXT_MIN)} characters about your experience.`);
      textRef.current?.focus();
      return;
    }
    if (settings.turnstileSiteKey && !turnstileToken) {
      setErr('Please complete the check above the button.');
      return;
    }
    setBusy(true);
    try {
      await api('POST', `/api/v1/reviews/${encodeURIComponent(token)}`, {
        rating,
        reviewerName: name.trim(),
        text: text.trim(),
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IP_RULE') {
        const body = error.body as { ipBlock?: IpBlockInfo };
        setBlocked(body.ipBlock ?? null);
      }
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wrap page-pad">
      <div className="card review-card">
        <div className="tiny muted">Review for</div>
        <h1 className="h2">{a.name}</h1>
        {a.practiceName && <div className="muted small">{a.practiceName}</div>}

        {blocked ? (
          <div className="alert alert-warn mt16">
            A review was already submitted from your network on{' '}
            {longDate(new Date(blocked.blockedAt))}. Only one review per connection is allowed every{' '}
            {blocked.windowDays} days — you can leave another review from{' '}
            {longDate(new Date(blocked.allowedFrom))}.
          </div>
        ) : (
          <form className="form mt16" onSubmit={(e) => void submit(e)} noValidate>
            <fieldset className="plain-fieldset">
              <legend className="label">Your rating</legend>
              <div
                className="star-pick"
                onMouseLeave={() => {
                  setHoverR(0);
                }}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    className={(hoverR || rating) >= n ? 'on' : ''}
                    onMouseEnter={() => {
                      setHoverR(n);
                    }}
                    onClick={() => {
                      setRating(n);
                    }}
                    aria-label={`${String(n)} star${n === 1 ? '' : 's'}`}
                    aria-pressed={rating === n}
                    aria-disabled={!hydrated}
                  >
                    ★
                  </button>
                ))}
              </div>
            </fieldset>
            <label>
              Your name
              <input
                ref={nameRef}
                value={name}
                maxLength={REVIEWER_NAME_MAX}
                autoComplete="name"
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="e.g. Priya S."
              />
            </label>
            <label>
              Your experience
              <textarea
                ref={textRef}
                rows={4}
                value={text}
                aria-describedby="review-length"
                maxLength={REVIEW_TEXT_MAX}
                onChange={(e) => {
                  setText(e.target.value);
                }}
                placeholder="How was your consultation? What did you find helpful?"
              />
              <span className="tiny muted" id="review-length">
                Write at least {REVIEW_TEXT_MIN} characters · {text.trim().length}/{REVIEW_TEXT_MAX}
              </span>
            </label>
            <Turnstile siteKey={settings.turnstileSiteKey} onToken={setTurnstileToken} />
            {err && (
              <div className="alert alert-error" role="alert">
                {err}
              </div>
            )}
            {/* type="button" (LANE-D-32): a native submit button, tapped before the page's
                JavaScript has taken over, reloads the page and loses the name and review typed
                in. This only ever submits through the click handler, once React is listening. */}
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? 'Publishing…' : 'Publish review'}
            </button>
            <p className="tiny muted center">
              Your review appears on {a.name}&apos;s public profile with your name. This link works
              only once.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
